import { Image as ImageIcon } from "lucide-react";
import {
  PageHeading, Section, Field, Callout, RoleBadge,
  Diagram, MockField, MockButton, MockRow,
} from "../shared";

export default function SettingsBrandingSection() {
  return (
    <div data-testid="guide-settings-branding">
      <PageHeading
        icon={ImageIcon}
        title="Branding"
        description="Make the system look like your organisation: set the company name, department name, logo, and the web address. These show up on the sign-in page, the top header, and inside every email."
      />

      <div className="flex items-center gap-2 mb-4">
        <RoleBadge role="it_admin" />
        <span className="text-xs text-white/40">Needs the <span className="font-mono text-white/70">manage_branding</span> permission.</span>
      </div>

      <Callout variant="info" title="In plain words">
        This is the cosmetic page. Whatever you type here is what people see when they sign in and in the emails the system sends. There's nothing risky here except the Base URL, which we explain below.
      </Callout>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          When you first brand the system for your organisation, when the logo changes, or whenever the system moves to a new web address (then you must update the Base URL).
        </p>
      </Section>

      <Section title="The screen">
        <Diagram
          frameLabel="Settings → Branding"
          legend={[
            <>Type the company name shown at the top of the sign-in page and header.</>,
            <>Optionally add a department name underneath it.</>,
            <>Upload a logo image (PNG or JPG, up to 300 KB).</>,
            <>Set the Base URL — the web address your people actually visit.</>,
          ]}
        >
          <MockField label="Company name" value="Example Holdings" marker={1} />
          <MockField label="Department name" value="Investments Office" marker={2} />
          <MockRow>
            <MockButton label="Upload logo" marker={3} variant="ghost" />
          </MockRow>
          <MockField label="Base URL" value="https://governance.example.com" marker={4} />
        </Diagram>
      </Section>

      <Section title="Each field explained">
        <div className="space-y-2">
          <Field name="Company name" required="required"><p>The top line on the sign-in page and in the title bar, and the fallback "from" name on emails.</p></Field>
          <Field name="Department name" required="optional"><p>A smaller line under the company name. Handy when one company runs more than one copy of the system.</p></Field>
          <Field name="Logo" required="optional"><p>A PNG or JPG up to <span className="text-white">300 KB</span>. Shown on the sign-in page and at the top of emails.</p></Field>
          <Field name="Base URL" required="required"><p>The web address people use to reach the system (e.g. <span className="text-white">https://governance.example.com</span>). It's put into every password-reset email and other email links. If it's wrong, those links won't work.</p></Field>
          <Field name="File uploads enabled" required="optional"><p>The on/off switch for attaching files to action items. <span className="text-white">On by default.</span> Turn it off if your organisation prefers to keep documents elsewhere — the upload box disappears from every form.</p></Field>
        </div>
      </Section>

      <Callout variant="warning" title="The Base URL must match where people actually go">
        If your people visit <span className="text-white">https://gov.example.com</span> but the Base URL says <span className="text-white">http://localhost:5000</span>, every password-reset link will be broken. Update the Base URL whenever the system moves.
      </Callout>
      <Callout variant="info" title="The logo is visible before sign-in">
        The sign-in page needs the logo before anyone logs in, so the company name, department name, and logo are public on purpose. Everything else stays private to admins.
      </Callout>
    </div>
  );
}
